import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from "@angular/router";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { CATALOGUE_HOME_ROUTE } from "@core/consts/assignment-page.constants";
import { CoursePrerequisiteAccessStatusEnum } from "@core/enums/course-prerequisite-access-status.enum";
import type { CoursePrerequisiteAccessResult } from "@core/models/course-prerequisite-access.model";
import { UserAssignmentNavigationService } from "@core/services/user-assignment-navigation.service";
import { parseRouteParamNumber } from "@core/utils/parse-route-param-number.util";
import { CoursePrerequisiteAccessService } from "@shared/services/course-prerequisite-access/course-prerequisite-access.service";

export const CoursePrerequisiteAccessGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
): Observable<boolean | UrlTree> => {
  const coursePrerequisiteAccessService: CoursePrerequisiteAccessService = inject(
    CoursePrerequisiteAccessService,
  );
  const userAssignmentNavigation: UserAssignmentNavigationService = inject(
    UserAssignmentNavigationService,
  );
  const router: Router = inject(Router);

  const blockedRedirectUrlTree: (id: number) => UrlTree = (id: number): UrlTree =>
    router.parseUrl(userAssignmentNavigation.courseDetailsBackRoute(id));

  const courseId: number | null = parseRouteParamNumber(route, "courseId");

  if (courseId === null || courseId <= 0) return of(router.createUrlTree([CATALOGUE_HOME_ROUTE]));

  return coursePrerequisiteAccessService.checkAccess$(courseId).pipe(
    map((result: CoursePrerequisiteAccessResult): boolean | UrlTree =>
      result.status === CoursePrerequisiteAccessStatusEnum.ACCESSIBLE
        ? true
        : blockedRedirectUrlTree(courseId),
    ),
    catchError(() => of(blockedRedirectUrlTree(courseId))),
  );
};
